import AppContext from 'App/AppContext';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { CopilotLibraryEditorContextKey } from 'models/copilot/copilotBridge';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import {
  CopilotDocumentIngest,
  CopilotDocumentIngestSourceType,
  CopilotDocumentIngestStatus,
  CopilotDocumentStorageSourceType,
} from 'models/copilot/copilotDocumentIngest';
import {
  LibraryDocumentInstructionEditRestartMode,
  LibraryDocumentInstructionEditRunStatus,
  LibraryDocumentInstructionEditStatus,
} from 'models/copilot/libraryDocumentInstructionEditProcess';
import { createContext, lazy, ReactNode, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  apiClaimLibraryDocumentInstructionEditProcess,
  apiCompleteLibraryDocumentInstructionEditProcess,
  apiCopilotReferenceLibraryDocument,
  apiFailLibraryDocumentInstructionEditProcess,
  apiGetLibraryDocumentInstructionEditProcess,
  apiHeartbeatLibraryDocumentInstructionEditProcess,
  apiMarkSavingLibraryDocumentInstructionEditProcess,
  apiStartLibraryDocumentInstructionEditProcess,
} from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { CopilotAgentSessionSeed } from '../../Agent/useCopilotAgentSession';
import { getDocumentBlockFamilies } from './copilotDocumentBlockHelpers';
import { DocumentExecutionResult } from './DocumentExecutionHost';
import {
  LibraryDriveCoordinates,
  readLibrarySharePointVersion,
  resolveLibraryDriveCoordinates,
  uploadDocxBufferToLibrary,
} from './documentSaveHelpers';

const DocumentExecutionHost = lazy(() => import('./DocumentExecutionHost'));

// Refresh the worker heartbeat well within the backend stale threshold (2 min) so a healthy run is never
// judged interrupted.
const HEARTBEAT_INTERVAL_MS = 45000;

// Safety net: a single background run should never take longer than this. If it does (or it silently stalls
// and never reports completion), the watchdog fails the run, surfaces a toast, and frees the queue so the tab
// is not left with an invisible ghost run. Generous so it never kills a legitimately long edit.
const WATCHDOG_TIMEOUT_MS = 8 * 60 * 1000;

// Optional deeplink shown as a clickable link in the success toast when the background edit completes. The
// caller supplies it at enqueue time (kept generic so a future caller can point it at a Risk, Control, etc.).
// `href` is an in-app route navigated via the router (e.g. '/library/links?id=123').
export interface DocumentJobCompletionLink {
  label: string;
  href: string;
}

export interface DocumentJobHostContextValue {
  // Latest tenant-scoped process status for a document, used by callers to warn before starting.
  getJobForDocument: (externalId: string) => Promise<LibraryDocumentInstructionEditStatus | undefined>;
  // Queue a background edit for a document. Warns/confirms restart when a process already exists. The optional
  // completion link is shown in the success toast when the edit finishes.
  enqueueByExternalId: (externalId: string, completionLink?: DocumentJobCompletionLink) => Promise<void>;
  isBusy: boolean;
}

const DocumentJobHostContext = createContext<DocumentJobHostContextValue>({
  getJobForDocument: async () => undefined,
  enqueueByExternalId: async () => undefined,
  isBusy: false,
});

export const useDocumentJobHost = (): DocumentJobHostContextValue => useContext(DocumentJobHostContext);

interface ActiveDocumentJob {
  externalId: string;
  runId: string;
  libraryLinkId: number;
  title?: string;
  coordinates: LibraryDriveCoordinates;
  block: CopilotConversationContentBlock;
  session: CopilotAgentSessionSeed;
  pendingInstruction: string;
  instructionExternalId?: string;
  contextKey: string;
  completionLink?: DocumentJobCompletionLink;
}

interface QueuedDocumentJob {
  externalId: string;
  completionLink?: DocumentJobCompletionLink;
}

interface PendingConfirm {
  externalId: string;
  title: string;
  message: string;
  completionLink?: DocumentJobCompletionLink;
}

// Off-screen container: SuperDoc measures layout, so the host needs real dimensions. Never display:none or 0x0.
const offScreenHostStyle: React.CSSProperties = {
  position: 'fixed',
  left: -100000,
  top: 0,
  width: 1200,
  height: 900,
  visibility: 'hidden',
  pointerEvents: 'none',
};

// App-wide provider that runs one background library document edit at a time in a persistent off-screen host.
// The backend owns durable process state; this provider drives the browser execution and SharePoint save-back.
export const DocumentJobHostProvider = (props: { children: ReactNode }): JSX.Element => {
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  // the background edit runs in the editor's own session, never the panel's chat
  const contextKey = CopilotLibraryEditorContextKey;

  const [activeJob, setActiveJob] = useState<ActiveDocumentJob | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | undefined>(undefined);

  const activeJobRef = useRef<ActiveDocumentJob | undefined>(undefined);
  activeJobRef.current = activeJob;
  const queueRef = useRef<QueuedDocumentJob[]>([]);
  const processingRef = useRef<boolean>(false);
  const finalizingRef = useRef<boolean>(false);
  const jobResolveRef = useRef<(() => void) | undefined>(undefined);
  const heartbeatTimerRef = useRef<number | undefined>(undefined);
  const watchdogTimerRef = useRef<number | undefined>(undefined);

  const getAccessToken = useCallback(() => appContext.getAccessToken(apiRequest.scopes), [appContext]);

  const getJobForDocument = useCallback(
    async (externalId: string): Promise<LibraryDocumentInstructionEditStatus | undefined> => {
      try {
        const accessToken = await getAccessToken();

        return await apiGetLibraryDocumentInstructionEditProcess(externalId, accessToken);
      } catch (err) {
        logger.debug('DocumentJobHostProvider.getJobForDocument failed', err);

        return undefined;
      }
    },
    [getAccessToken],
  );

  // Clears both the heartbeat interval and the watchdog timeout for the active run.
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== undefined) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = undefined;
    }
    if (watchdogTimerRef.current !== undefined) {
      window.clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = undefined;
    }
  }, []);

  // Clear the heartbeat interval and watchdog timeout if the provider unmounts mid-run, so neither timer
  // keeps firing (and calling setActiveJob / getAccessToken) against an unmounted tree.
  useEffect(() => () => stopHeartbeat(), [stopHeartbeat]);

  // Resolve the awaiting runJob and clear the active host. Runs exactly once per job.
  const finishActiveJob = useCallback(() => {
    stopHeartbeat();
    setActiveJob(undefined);
    activeJobRef.current = undefined;
    const resolve = jobResolveRef.current;
    jobResolveRef.current = undefined;
    finalizingRef.current = false;
    resolve?.();
  }, [stopHeartbeat]);

  // Host run finished. Guarded stage: mark saving BEFORE uploading so a superseded run never writes a
  // competing SharePoint version. On any failure the process is marked Failed so it is never left dangling.
  const handleRunComplete = useCallback(
    async (result: DocumentExecutionResult) => {
      const job = activeJobRef.current;
      if (!job || finalizingRef.current) return;

      finalizingRef.current = true;
      stopHeartbeat();

      const accessToken = await getAccessToken();
      try {
        if (!result.ok) {
          await apiFailLibraryDocumentInstructionEditProcess(job.libraryLinkId, job.runId, result.error, accessToken);
          appContext.showNotification(t('copilot:Chat.Document.Background.Failed'), true);

          return;
        }

        const buffer = await result.getContent?.();
        if (!buffer) {
          await apiFailLibraryDocumentInstructionEditProcess(
            job.libraryLinkId,
            job.runId,
            'No document content',
            accessToken,
          );
          appContext.showNotification(t('copilot:Chat.Document.Background.Failed'), true);

          return;
        }

        // Saving gate: if the run was superseded, abort WITHOUT uploading.
        const saving = await apiMarkSavingLibraryDocumentInstructionEditProcess(
          job.libraryLinkId,
          job.runId,
          accessToken,
        );
        if (!saving?.applied) {
          appContext.showNotification(t('copilot:Chat.Document.Background.Superseded'), false, true);

          return;
        }

        let saved = await uploadDocxBufferToLibrary(appContext, job.coordinates, buffer, job.title);
        if (!saved.version) {
          saved = await readLibrarySharePointVersion(appContext, job.coordinates);
        }

        const completed = await apiCompleteLibraryDocumentInstructionEditProcess(
          job.libraryLinkId,
          job.runId,
          saved.version ?? '',
          saved.modified,
          accessToken,
        );
        if (completed?.applied) {
          // Success toast with the caller-provided deeplink (e.g. "Open document" -> the library document).
          const link = job.completionLink;
          appContext.showNotification(
            t('copilot:Chat.Document.Background.Success'),
            false,
            false,
            false,
            link ? { text: link.label, onClick: () => history.push(link.href) } : undefined,
          );
        } else {
          appContext.showNotification(t('copilot:Chat.Document.Background.Superseded'), false, true);
        }
      } catch (err) {
        try {
          await apiFailLibraryDocumentInstructionEditProcess(
            job.libraryLinkId,
            job.runId,
            AppError.fromApiError(err).message,
            accessToken,
          );
        } catch (failErr) {
          logger.debug('DocumentJobHostProvider: fail transition errored', failErr);
        }
        appContext.showNotification(t('copilot:Chat.Document.Background.Failed'), true);
      } finally {
        finishActiveJob();
      }
    },
    [appContext, getAccessToken, stopHeartbeat, finishActiveJob, history, t],
  );

  const startHeartbeat = useCallback(
    (libraryLinkId: number, runId: string) => {
      stopHeartbeat();
      heartbeatTimerRef.current = window.setInterval(async () => {
        try {
          const accessToken = await getAccessToken();
          const beat = await apiHeartbeatLibraryDocumentInstructionEditProcess(libraryLinkId, runId, accessToken);
          if (beat && !beat.applied && !finalizingRef.current) {
            // A newer run took over; stop this host without uploading.
            finalizingRef.current = true;
            appContext.showNotification(t('copilot:Chat.Document.Background.Superseded'), false, true);
            finishActiveJob();
          }
        } catch (err) {
          logger.debug('DocumentJobHostProvider: heartbeat failed', err);
        }
      }, HEARTBEAT_INTERVAL_MS);

      // Watchdog: if the run never reports completion (silent stall) within the cap, fail it and free the
      // queue so the tab is not left with an invisible ghost run.
      watchdogTimerRef.current = window.setTimeout(async () => {
        if (finalizingRef.current) return;

        finalizingRef.current = true;
        stopHeartbeat();
        try {
          const accessToken = await getAccessToken();
          await apiFailLibraryDocumentInstructionEditProcess(
            libraryLinkId,
            runId,
            'Background edit timed out',
            accessToken,
          );
        } catch (err) {
          logger.debug('DocumentJobHostProvider: watchdog fail transition errored', err);
        }
        appContext.showNotification(t('copilot:Chat.Document.Background.TimedOut'), true);
        finishActiveJob();
      }, WATCHDOG_TIMEOUT_MS);
    },
    [appContext, getAccessToken, stopHeartbeat, finishActiveJob, t],
  );

  // Prepare + start + ingest + claim a single job, then mount the host and await its completion.
  const runJob = useCallback(
    async (
      externalId: string,
      restartMode: LibraryDocumentInstructionEditRestartMode,
      completionLink?: DocumentJobCompletionLink,
    ) => {
      const accessToken = await getAccessToken();

      const coordinatesReady = await (async () => {
        try {
          const startResult = await apiStartLibraryDocumentInstructionEditProcess(externalId, restartMode, accessToken);
          if (!startResult) {
            appContext.showNotification(t('copilot:Chat.Document.Background.PrepareFailed'), true);

            return undefined;
          }
          if (startResult.conflict || !startResult.started || !startResult.runId || !startResult.libraryLinkId) {
            // Lost a race to another run; the caller already warned. Stop quietly.
            appContext.showNotification(t('copilot:Chat.Document.Background.Superseded'), false, true);

            return undefined;
          }

          const coordinates = await resolveLibraryDriveCoordinates(appContext, startResult.libraryLinkId);
          if (!coordinates) {
            await apiFailLibraryDocumentInstructionEditProcess(
              startResult.libraryLinkId,
              startResult.runId,
              'Document is not a SharePoint drive item',
              accessToken,
            );
            appContext.showNotification(t('copilot:Chat.Document.Background.PrepareFailed'), true);

            return undefined;
          }

          return { startResult, coordinates };
        } catch (err) {
          appContext.showNotification(AppError.fromApiError(err).message, true);

          return undefined;
        }
      })();

      if (!coordinatesReady) return;

      const { startResult, coordinates } = coordinatesReady;
      const libraryLinkId = startResult.libraryLinkId as number;
      const runId = startResult.runId as string;
      // Send only the instruction reference; the backend resolves the instruction from the database and
      // runs its text. It is consumed once by the agent session (never resent on follow-up turns).
      const instructionExternalId = externalId;

      // Ingest the library document into a fresh Copilot session so the agent has the docx tools and content.
      const ingestRequest = new CopilotDocumentIngest();
      ingestRequest.contextKey = contextKey;
      ingestRequest.sourceType = CopilotDocumentIngestSourceType.Library;
      ingestRequest.storageSourceType = CopilotDocumentStorageSourceType.SharePoint;
      ingestRequest.libraryLinkId = libraryLinkId;
      ingestRequest.libraryListId = startResult.libraryListId;
      ingestRequest.title = startResult.title;

      let block: CopilotConversationContentBlock | undefined;
      let session: CopilotAgentSessionSeed | undefined;
      try {
        const ingest = await apiCopilotReferenceLibraryDocument(ingestRequest, accessToken);
        const content = ingest?.content;
        if (
          ingest?.status === CopilotDocumentIngestStatus.Ready &&
          content?.conversation &&
          content.sessionId !== undefined
        ) {
          block = getDocumentBlockFamilies(content.conversation)[0]?.latest;
          if (block) session = { sessionId: content.sessionId, conversation: content.conversation };
        }
      } catch (err) {
        logger.debug('DocumentJobHostProvider: ingest failed', err);
      }

      if (!block || !session) {
        await apiFailLibraryDocumentInstructionEditProcess(libraryLinkId, runId, 'Ingest failed', accessToken);
        appContext.showNotification(t('copilot:Chat.Document.Background.PrepareFailed'), true);

        return;
      }

      const claim = await apiClaimLibraryDocumentInstructionEditProcess(
        libraryLinkId,
        runId,
        session.sessionId,
        accessToken,
      );
      if (!claim?.applied) {
        appContext.showNotification(t('copilot:Chat.Document.Background.Superseded'), false, true);

        return;
      }

      // Seed the host with block + session + instruction as ONE object so the seeded session never mounts
      // with a stale-undefined session id (legacy ReactDOM render has no automatic batching).
      const job: ActiveDocumentJob = {
        externalId,
        runId,
        libraryLinkId,
        title: startResult.title,
        coordinates,
        block,
        session,
        pendingInstruction: startResult.pendingInstruction ?? '',
        instructionExternalId,
        contextKey,
        completionLink,
      };

      await new Promise<void>((resolve) => {
        jobResolveRef.current = resolve;
        activeJobRef.current = job;
        setActiveJob(job);
        startHeartbeat(libraryLinkId, runId);
        appContext.showNotification(t('copilot:Chat.Document.Background.Started'));
      });
    },
    [appContext, contextKey, getAccessToken, startHeartbeat, t],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;

    processingRef.current = true;
    setIsBusy(true);
    try {
      while (queueRef.current.length > 0) {
        const queued = queueRef.current.shift() as QueuedDocumentJob;
        // A queued item is always an approved (re)start: the caller confirmed restart when needed.
        await runJob(queued.externalId, LibraryDocumentInstructionEditRestartMode.Restart, queued.completionLink);
      }
    } finally {
      processingRef.current = false;
      setIsBusy(false);
    }
  }, [runJob]);

  const enqueueConfirmed = useCallback(
    (externalId: string, completionLink?: DocumentJobCompletionLink) => {
      queueRef.current.push({ externalId, completionLink });
      void processQueue();
    },
    [processQueue],
  );

  const enqueueByExternalId = useCallback(
    async (externalId: string, completionLink?: DocumentJobCompletionLink): Promise<void> => {
      const status = await getJobForDocument(externalId);

      // No prior process, or a terminal one: start directly.
      if (
        !status?.exists ||
        status.status === LibraryDocumentInstructionEditRunStatus.NotStarted ||
        status.status === LibraryDocumentInstructionEditRunStatus.Failed ||
        status.status === LibraryDocumentInstructionEditRunStatus.Canceled
      ) {
        enqueueConfirmed(externalId, completionLink);

        return;
      }

      if (status.status === LibraryDocumentInstructionEditRunStatus.Completed) {
        setPendingConfirm({
          externalId,
          completionLink,
          title: t('copilot:Chat.Document.Background.CompletedTitle'),
          message: t('copilot:Chat.Document.Background.CompletedRestart'),
        });

        return;
      }

      // InProgress: distinguish an interrupted run from a live one.
      setPendingConfirm({
        externalId,
        completionLink,
        title: t('copilot:Chat.Document.Background.ActiveTitle'),
        message: status.heartbeatStale
          ? t('copilot:Chat.Document.Background.InterruptedRestart')
          : t('copilot:Chat.Document.Background.ActiveRestart'),
      });
    },
    [getJobForDocument, enqueueConfirmed, t],
  );

  const contextValue: DocumentJobHostContextValue = {
    getJobForDocument,
    enqueueByExternalId,
    isBusy,
  };

  return (
    <DocumentJobHostContext.Provider value={contextValue}>
      {props.children}
      {activeJob && (
        <div style={offScreenHostStyle} aria-hidden="true">
          <Suspense fallback={<div />}>
            <DocumentExecutionHost
              key={activeJob.runId}
              block={activeJob.block}
              session={activeJob.session}
              pendingInstruction={activeJob.pendingInstruction}
              instructionExternalId={activeJob.instructionExternalId}
              contextKey={activeJob.contextKey}
              onComplete={handleRunComplete}
            />
          </Suspense>
        </div>
      )}
      <DialogYesNo
        hidden={pendingConfirm === undefined}
        title={pendingConfirm?.title ?? ''}
        subText={pendingConfirm?.message ?? ''}
        onYes={() => {
          const confirmed = pendingConfirm;
          setPendingConfirm(undefined);
          if (confirmed) enqueueConfirmed(confirmed.externalId, confirmed.completionLink);
        }}
        onNo={() => setPendingConfirm(undefined)}
      />
    </DocumentJobHostContext.Provider>
  );
};

export default DocumentJobHostProvider;
